import { type IResponseContact } from 'entities/Contacts'
import { type IResponseTag } from 'entities/Tags'
import { type IResponseInbox } from 'entities/Inbox/api/types'
import { type IResponseConversation } from 'entities/Conversation/api/types'
import {
  type ComplianceCategory,
  type ComplianceStatus,
} from 'entities/WebMcp/lib/complianceSignals'
import {
  type OnboardingStatus,
  type OnboardingStepKey,
  type OnboardingStepStatus,
} from 'entities/WebMcp/lib/onboardingSignals'

export type ContactSummary = {
  id: number
  name: string
  number: string
  email: string | null
  tags: string[]
}

export type ContactDetail = ContactSummary & {
  first_name: string | null
  last_name: string | null
  opted_out: boolean
  created_at: string
  notes_count: number
}

export const serializeContactSummary = (c: IResponseContact): ContactSummary => ({
  id: c.id,
  name: c.full_name,
  number: c.formatted_number || c.number,
  email: c.email,
  // Dedup by tag id: the backend franchise tag-attach path can create duplicate
  // pivot rows (no unique constraint), so guard the read side from surfacing dupes.
  tags: [...new Map((c.tags ?? []).map((t) => [t.id, t.label] as const)).values()],
})

export const serializeContactDetail = (c: IResponseContact): ContactDetail => ({
  ...serializeContactSummary(c),
  first_name: c.first_name,
  last_name: c.last_name,
  // opt_out is a status string ('hard'/'soft') when opted out, null otherwise
  // (mirrors the Contact model: opted-in iff opt_out === null).
  opted_out: c.opt_out !== null,
  created_at: c.created_at,
  notes_count: (c.notes ?? []).length,
})

export type TagSummary = { id: number; label: string; contacts_count?: number }

export const serializeTag = (t: IResponseTag): TagSummary => ({
  id: t.id,
  label: t.label,
  // Let the agent see an empty tag before it picks one for a broadcast audience.
  // The field is optional on the API; omit it entirely when absent rather than
  // reporting a misleading zero.
  ...(typeof t.contacts_count === 'number' && { contacts_count: t.contacts_count }),
})

export type InboxSummary = {
  id: number
  name: string
  eligible: boolean
  number_id: number | null
}

export const serializeInbox = (i: IResponseInbox): InboxSummary => ({
  id: i.id,
  name: i.name,
  // Can send if it's an RCS sender or has at least one phone number attached.
  eligible:
    i.messaging_channel_type === 'rcs_sender' || Boolean(i.number) || (i.numbers?.length ?? 0) > 0,
  // For broadcast send_from; send_message uses the inbox id as team_id directly.
  number_id: i.number?.id ?? i.numbers?.[0]?.id ?? null,
})

export type ConversationSummary = {
  id: number
  contact: string
  unread_count: number
  last_message: string | null
}

export const serializeConversationSummary = (
  c: IResponseConversation,
  // messages/search returns conversations without an embedded contact — the names
  // live in a sibling `contacts` array, keyed by id.
  contactsById?: Map<number, IResponseContact>
): ConversationSummary => {
  const contact =
    c.contact ??
    (c.participants ?? []).find((p) => p.id === c.contact_id) ??
    contactsById?.get(c.contact_id)

  return {
    id: c.id,
    contact:
      contact?.full_name ||
      contact?.formatted_number ||
      contact?.number ||
      `Contact #${c.contact_id}`,
    unread_count: c.unread_count,
    last_message: c.recent_message?.body ?? null,
  }
}

export type OnboardingStepSummary = {
  key: OnboardingStepKey
  title: string
  status: OnboardingStepStatus
  signal: string
}

export type OnboardingStatusSummary = {
  steps: OnboardingStepSummary[]
  remainingCount: number
  unknownCount: number
  allComplete: boolean
}

export const serializeOnboardingStatus = (s: OnboardingStatus): OnboardingStatusSummary => ({
  // Drop the internal numeric id; the model reasons over key/title/status/signal.
  steps: s.steps.map(({ key, title, status, signal }) => ({ key, title, status, signal })),
  remainingCount: s.remainingCount,
  unknownCount: s.unknownCount,
  allComplete: s.allComplete,
})

export type ComplianceCategorySummary = Omit<ComplianceCategory, 'submittedAt'> & {
  submittedAt?: string
}

export type ComplianceStatusSummary = {
  categories: ComplianceCategorySummary[]
  blocker: ComplianceStatus['blocker']
  channels: ComplianceStatus['channels']
  unreadable: boolean
}

export const serializeComplianceStatus = (s: ComplianceStatus): ComplianceStatusSummary => ({
  categories: s.categories.map(({ submittedAt, ...category }) => ({
    ...category,
    // Omit the field entirely when there is no submission, rather than sending a
    // null the model may render as an empty date.
    ...(submittedAt && { submittedAt }),
  })),
  blocker: s.blocker,
  channels: s.channels,
  unreadable: s.unreadable,
})
